import { createSelector } from "@reduxjs/toolkit";
import { CustomerMigrationTypes } from "@nxplatform/platform-entities/customer/customer-enum-types";
import { LIMITS_FEATURE_KEY } from "@nxplatform/platform-entities/customer/limits";
import { OddsFormatOptions, displayBalance } from "@nxplatform/platform-entities/customer/preferences";
import { selectExchangeRates } from "../geo-data/shared-geo-data-selectors.js";
import { getPlatformSettingsState, selectCryptoCurrencyEnabled, selectNotificationsEnabled, selectNonAuthTimestamp } from "../platform-settings/shared-platform-settings-selectors.js";
import { CUSTOMER_TOKEN_FEATURE_KEY, CUSTOMER_FEATURE_KEY, CUSTOMER_PREFERENCES_FEATURE_KEY, BASIC_MODAL_FEATURE_KEY, USER_CONFIG_FEATURE_KEY, TERMS_AND_POLICY_FEATURE_KEY, CASHOUT_FEATURE_KEY, SNACKBAR_NOTIFICATION_FEATURE_KEY, CUSTOMER_BET_HISTORY_FEATURE_KEY } from "./shared-customer-feature-keys.js";
const EMPTY_STATE = {};
const getCustomerState = (rootState) => rootState[CUSTOMER_FEATURE_KEY] || EMPTY_STATE;
const getUserConfigState = (rootState) => rootState[USER_CONFIG_FEATURE_KEY] || EMPTY_STATE;
const getCustomerLimitsState = (rootState) => rootState[LIMITS_FEATURE_KEY] || EMPTY_STATE;
const getCustomerTokenState = (rootState) => rootState[CUSTOMER_TOKEN_FEATURE_KEY] || EMPTY_STATE;
const getTermsAndPolicyState = (rootState) => rootState[TERMS_AND_POLICY_FEATURE_KEY] || EMPTY_STATE;
const getBasicModalState = (rootState) => rootState[BASIC_MODAL_FEATURE_KEY] || EMPTY_STATE;
const getCashoutState = (rootState) => rootState[CASHOUT_FEATURE_KEY] || EMPTY_STATE;
const getSnackbarNotificationState = (rootState) => rootState[SNACKBAR_NOTIFICATION_FEATURE_KEY] || EMPTY_STATE;
const getCustomerBetHistoryState = (rootState) => rootState[CUSTOMER_BET_HISTORY_FEATURE_KEY] || EMPTY_STATE;
const selectCustomerTokenEntities = createSelector(
  getCustomerTokenState,
  (s) => s == null ? void 0 : s.entities
);
const selectCustomerEntities = createSelector(
  getCustomerState,
  (s) => s.entities || EMPTY_STATE
);
const selectIsGBGStatusLoaded = createSelector(
  getCustomerState,
  (state) => {
    const { entities: { gbg } = {} } = state;
    const { isGBGCheckExist, isNeedOndatoVerify } = gbg || {};
    return typeof isGBGCheckExist === "boolean" && typeof isNeedOndatoVerify === "boolean";
  }
);
const selectIsGBGVerificationNeeded = createSelector(
  [getCustomerState, selectIsGBGStatusLoaded],
  (state, gbgStatusLoaded) => {
    const { entities: { gbg } = {} } = state;
    const { isGBGCheckExist, isNeedOndatoVerify } = gbg || {};
    return gbgStatusLoaded ? isGBGCheckExist && isNeedOndatoVerify : null;
  }
);
const selectCustomerMigrationActiveStep = createSelector(
  selectCustomerEntities,
  ({ customerMigrationActiveStep }) => customerMigrationActiveStep
);
const selectIsOnDatoMigrationInProgress = createSelector(
  selectCustomerMigrationActiveStep,
  (customerMigrationStep) => [
    CustomerMigrationTypes.ONTARIO_MIGRATION_START,
    CustomerMigrationTypes.ONTARIO_OCCUPATION_FORM
  ].includes(customerMigrationStep)
);
const selectIsGviveMigrationInProgress = createSelector(
  selectCustomerMigrationActiveStep,
  (customerMigrationStep) => customerMigrationStep === CustomerMigrationTypes.GVIVE_MIGRATION
);
const selectCustomerLimitsTokenSuccess = createSelector(
  getCustomerLimitsState,
  (s) => s.tokenSuccess
);
const selectIsGBGStatusNotSet = createSelector(
  [getCustomerState, selectIsGBGStatusLoaded],
  (state, gbgStatusLoaded) => {
    const { entities: { gbg } = {} } = state;
    const { isGBGCheckExist } = gbg || {};
    return gbgStatusLoaded && !isGBGCheckExist;
  }
);
const selectCustomerBalanceInfo = createSelector(
  getCustomerState,
  (s) => {
    var _a, _b, _c;
    return ((_a = s == null ? void 0 : s.entities) == null ? void 0 : _a.accounts) ? (_c = (_b = s == null ? void 0 : s.entities) == null ? void 0 : _b.accounts) == null ? void 0 : _c.find((acc) => acc.is_main) : null;
  }
);
const selectWebsocketHandledNotifications = createSelector(
  getCustomerState,
  (s) => s.handledNotifications
);
const selectCustomerMainCurrency = createSelector(
  selectCustomerBalanceInfo,
  (s) => s == null ? void 0 : s.currency
);
const getCustomerPreferencesState = (rootState) => rootState[CUSTOMER_PREFERENCES_FEATURE_KEY] || EMPTY_STATE;
const selectCustomerPreferencesEntities = createSelector(
  getCustomerPreferencesState,
  (s) => s.entities || EMPTY_STATE
);
const selectQuickBetEnabled = createSelector(
  selectCustomerPreferencesEntities,
  (s) => {
    var _a;
    return (_a = s.quickBet) == null ? void 0 : _a.enabled;
  }
);
const selectQuickBetSnackBar = createSelector(
  selectCustomerPreferencesEntities,
  (s) => {
    var _a;
    return (_a = s.quickBet) == null ? void 0 : _a.confirmationSnack;
  }
);
const selectQuickBetTimer = createSelector(
  selectCustomerPreferencesEntities,
  (s) => {
    var _a;
    return (_a = s.quickBet) == null ? void 0 : _a.timer;
  }
);
const selectAllQuickBetSums = createSelector(
  getCustomerPreferencesState,
  (s) => s.quickBetSums || EMPTY_STATE
);
const selectQuickBetSum = createSelector(
  [
    selectAllQuickBetSums,
    (selectAllQuickBetSums2, currency) => currency
  ],
  (selectAllQuickBetSums2, currency) => {
    if (selectAllQuickBetSums2) {
      return selectAllQuickBetSums2[currency];
    }
  }
);
const selectCustomerBasicModalStatus = createSelector(
  getBasicModalState,
  (s) => s.openedBasicModal
);
const selectCustomerCountryCode = createSelector(
  getCustomerState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.entities) == null ? void 0 : _a.country) == null ? void 0 : _b.code;
  }
);
const selectShowCryptoCurrencies = createSelector(
  [getPlatformSettingsState, selectCustomerEntities, selectCryptoCurrencyEnabled],
  (s, customer, cryptoCurrencyEnabled) => {
    var _a;
    return (s == null ? void 0 : s.showCryptoCurrencies) && (((_a = customer == null ? void 0 : customer.allowedFeatures) == null ? void 0 : _a.isCryptoEnabled) || cryptoCurrencyEnabled);
  }
);
const selectCustomerAllowSmartico = createSelector(
  [selectCustomerEntities],
  (customer) => {
    var _a, _b;
    return ((_a = customer == null ? void 0 : customer.allowedFeatures) == null ? void 0 : _a.smartico) === void 0 ? true : (_b = customer == null ? void 0 : customer.allowedFeatures) == null ? void 0 : _b.smartico;
  }
);
const selectCustomerPreferencesOddsIndication = createSelector(
  selectCustomerPreferencesEntities,
  (preferences) => Number(preferences.odd_indication)
);
const selectCustomerBroadcastAllowed = createSelector(
  getCustomerState,
  (s) => s.broadcastAllowed
);
const selectCustomerPreferencesOddsFormat = createSelector(
  selectCustomerPreferencesEntities,
  (preferences) => Number(preferences.odds_format ?? OddsFormatOptions.ODDS_FORMAT_DECIMAL)
);
const selectCustomerPreferencesTimeZone = createSelector(
  selectCustomerPreferencesEntities,
  (preferences) => preferences.timezone
);
const selectIsBetBuilderEnabled = createSelector(
  getUserConfigState,
  (s) => {
    var _a;
    return (_a = s.sport) == null ? void 0 : _a.isBetBuilderEnabled;
  }
);
const selectCustomerPreferencesBalanceVisible = createSelector(
  selectCustomerPreferencesEntities,
  (preferences) => Number(preferences.display_balance ?? displayBalance.hide_balance)
);
const selectTermsAndPolicyPopupStatus = createSelector(
  [getTermsAndPolicyState, selectCustomerTokenEntities],
  ({ popupEnabled }, isUserAuthorized) => popupEnabled && !!isUserAuthorized
);
const selectCustomerNotificationCenterPreferences = createSelector(
  selectCustomerPreferencesEntities,
  ({ notificationsEnabled }) => notificationsEnabled
);
const showCustomerNotificationCenter = createSelector([
  selectCustomerNotificationCenterPreferences,
  selectNotificationsEnabled
], (enabledByCustomer, featureEnabledByCMS) => enabledByCustomer && featureEnabledByCMS);
const selectCustomerAcceptBonuses = createSelector(
  getCustomerState,
  (s) => {
    var _a;
    return (_a = s.entities) == null ? void 0 : _a.accept_bonuses;
  }
);
const selectCustomerBindingContracts = createSelector(
  getCustomerState,
  (s) => s.entities.bindingContracts
);
const selectPolicyVersion = createSelector(
  getTermsAndPolicyState,
  ({ policy }) => policy && !!policy.length && policy[0].version
);
const selectTermsAndConditionsVersion = createSelector(
  getTermsAndPolicyState,
  ({ terms }) => terms && !!terms.length && terms[0].version
);
const selectTimeZones = createSelector(
  getCustomerPreferencesState,
  (s) => s.timeZone
);
const selectCustomerPreferencesUpdating = createSelector(
  getCustomerPreferencesState,
  (s) => s.updating
);
const selectCustomerPreferencesTimeFormat = createSelector(
  selectCustomerPreferencesEntities,
  (preferences) => Number(preferences.time_format)
);
const getForceCountryChangeStatus = createSelector(
  getCustomerState,
  ({ entities = {} }) => entities.forceCountryChange
);
const selectTimeZone = createSelector(
  selectCustomerPreferencesEntities,
  selectNonAuthTimestamp,
  (preferences, nonAuthTimestamp) => (preferences == null ? void 0 : preferences.timezone) || (nonAuthTimestamp == null ? void 0 : nonAuthTimestamp.timeZone) || null
);
const selectTimeFormat = createSelector(
  selectCustomerPreferencesEntities,
  selectNonAuthTimestamp,
  (preferences, nonAuthTimestamp) => Number(preferences == null ? void 0 : preferences.time_format) || Number(nonAuthTimestamp == null ? void 0 : nonAuthTimestamp.timeFormat) || null
);
const selectCustomerUpdateState = createSelector(
  getCustomerState,
  (s) => s.update
);
const selectCustomerCurrencies = createSelector(
  getCustomerState,
  (s) => {
    var _a;
    return (_a = s.entities) == null ? void 0 : _a.accounts;
  }
);
const selectCustomerPreferencesBettingOptions = createSelector(
  selectCustomerPreferencesEntities,
  (s) => Number(s.changeLive)
);
const selectCustomerFastAmounts = createSelector(
  getCustomerState,
  (s) => s.fastAmounts
);
const selectIsCustomerWatchersChatEnabled = createSelector(
  getCustomerPreferencesState,
  ({ entities }) => {
    const { watchersChatEnabled } = entities || {};
    return watchersChatEnabled;
  }
);
const selectSmarticoUserAccountId = createSelector(
  [selectCustomerEntities, selectCustomerBalanceInfo],
  (user, account) => {
    const { smarticoUserIdPrefix } = user;
    const accountId = account == null ? void 0 : account.id;
    return smarticoUserIdPrefix ? `${smarticoUserIdPrefix}-${accountId}` : accountId;
  }
);
const selectSmarticoHashLoading = createSelector(
  getCustomerState,
  (s) => s.smarticoHashLoading
);
const selectCustomerSportCompPointResetAt = createSelector(
  getCustomerState,
  (s) => s.compPointSportResetAt
);
const selectFastAmounts = createSelector(
  [getPlatformSettingsState, selectCustomerMainCurrency],
  (s, currency) => {
    var _a;
    return [...(((_a = s.sportsbook) == null ? void 0 : _a.fastAmounts) || {})[currency] || []].sort((a, b) => a - b);
  }
);
const selectFastAmountsWithCurrency = createSelector(
  [
    selectCustomerFastAmounts,
    (customerFastAmounts, currency) => currency
  ],
  (customerFastAmounts, currency) => {
    var _a;
    return (_a = customerFastAmounts == null ? void 0 : customerFastAmounts.fastAmounts) == null ? void 0 : _a[currency];
  }
);
const selectCustomerAllLastBetSums = createSelector(
  getCustomerState,
  (s) => s.lastBetSums
);
const selectCustomerLastBetSum = createSelector(
  [
    selectCustomerAllLastBetSums,
    (customerAllLastBetSums, currency) => currency
  ],
  (customerAllLastBetSums, currency) => {
    if (customerAllLastBetSums == null ? void 0 : customerAllLastBetSums.sums) {
      return customerAllLastBetSums.sums[currency];
    }
  }
);
const selectCustomerLastBetSaving = createSelector(
  selectCustomerPreferencesEntities,
  (s) => s.lastBetSumEnabled
);
const selectBalanceCryptoAlternative = createSelector(
  getCustomerPreferencesState,
  (s) => s == null ? void 0 : s.cryptoAlternative
);
const selectCustomerPreferencesUpdated = createSelector(
  getCustomerPreferencesState,
  (s) => s.updated
);
const selectReverseAlternativeCurrencyRate = createSelector(
  [
    selectBalanceCryptoAlternative,
    selectExchangeRates,
    (s, baseCurrency) => baseCurrency,
    (s, baseCurrency, currencyCode) => currencyCode
  ],
  (alternative, rates, baseCurrency, currencyCode) => {
    const reverseRate = rates.find(
      (rate) => (rate.codeFrom === currencyCode || rate.codeFrom === alternative) && rate.codeTo === baseCurrency
    );
    if (reverseRate) {
      return {
        codeFrom: reverseRate.codeTo,
        codeTo: reverseRate.codeFrom,
        value: 1 / reverseRate.value
      };
    }
  }
);
const selectDirectAlternativeCurrencyRate = createSelector(
  [
    selectBalanceCryptoAlternative,
    selectExchangeRates,
    (s, baseCurrency) => baseCurrency
  ],
  (alternative, rates, baseCurrency) => rates.find(
    (rate) => rate.codeTo === alternative && rate.codeFrom === baseCurrency
  )
);
const selectSideAlternativeCurrencyRate = createSelector(
  [
    selectBalanceCryptoAlternative,
    selectExchangeRates,
    (s, baseCurrency) => baseCurrency
  ],
  (alternative, rates, baseCurrency) => {
    const sideRateFrom = rates.find(
      (rate) => rate.codeTo === alternative
    );
    if (sideRateFrom) {
      const sideRateTo = rates.find((rate) => rate.codeFrom === sideRateFrom.codeFrom && rate.codeTo === baseCurrency);
      if (sideRateTo) {
        return {
          codeFrom: baseCurrency,
          codeTo: alternative,
          value: sideRateFrom.value / sideRateTo.value
        };
      }
    }
  }
);
const selectAlternativeCurrency = createSelector(
  [
    selectDirectAlternativeCurrencyRate,
    selectReverseAlternativeCurrencyRate,
    selectSideAlternativeCurrencyRate
  ],
  (direct, reverse, side) => direct || reverse || side
);
const selectCustomerMainCurrencies = createSelector(
  getCustomerState,
  (s) => {
    var _a, _b;
    return (_b = (_a = s.entities) == null ? void 0 : _a.accounts) == null ? void 0 : _b.find((account) => account.is_main);
  }
);
const selectCustomerWidgetMaximized = createSelector(
  getCustomerState,
  (s) => s.widgetsMaximized
);
const selectCustomerCompPointsSport = createSelector(
  getCustomerState,
  (s) => s.sportCompPoints
);
const selectCustomerCasinoCompPointResetAt = createSelector(
  getCustomerState,
  (s) => s.compPointCasinoResetAt
);
const selectCustomerId = createSelector(
  getCustomerState,
  (s) => {
    var _a;
    return (_a = s.entities) == null ? void 0 : _a.id;
  }
);
const selectNeededConfirmation = createSelector(
  getCashoutState,
  (s) => s.withoutConfirmation
);
const selectCashoutProcessing = createSelector(
  getCashoutState,
  (s) => s.processing
);
const selectTimeoutLimitActive = createSelector(
  getCustomerLimitsState,
  (s) => {
    var _a;
    return (_a = s.timeoutLimit) == null ? void 0 : _a.timeoutLimitActive;
  }
);
const selectTimeoutLimitModalStatus = createSelector(
  getCustomerLimitsState,
  (s) => {
    var _a;
    return (_a = s.timeoutLimit) == null ? void 0 : _a.timeoutLimitModal;
  }
);
const selectCustomerLimitsLoaded = createSelector(
  getCustomerLimitsState,
  (s) => s.loaded
);
const selectCustomerLimitsCreated = createSelector(
  getCustomerLimitsState,
  (s) => s.created
);
const selectCustomerLimits = createSelector(
  getCustomerLimitsState,
  (s) => s.entities
);
const selectCustomerLimitById = createSelector(
  getCustomerLimitsState,
  (s, id) => id,
  (s, id) => {
    var _a;
    return (_a = s.activeEntity) == null ? void 0 : _a[id];
  }
);
const selectLimitPeriods = createSelector(
  getCustomerLimitsState,
  (s) => s.limitPeriods
);
const selectSnackbarNotificationStorageState = createSelector(
  getSnackbarNotificationState,
  (s) => s.saveInStorage
);
const selectCustomerBetHistoryPlayers = createSelector(
  getCustomerBetHistoryState,
  (s) => s.players
);
export {
  getBasicModalState,
  getCashoutState,
  getCustomerBetHistoryState,
  getCustomerLimitsState,
  getCustomerPreferencesState,
  getCustomerState,
  getCustomerTokenState,
  getForceCountryChangeStatus,
  getSnackbarNotificationState,
  getTermsAndPolicyState,
  getUserConfigState,
  selectAllQuickBetSums,
  selectAlternativeCurrency,
  selectBalanceCryptoAlternative,
  selectCashoutProcessing,
  selectCustomerAcceptBonuses,
  selectCustomerAllLastBetSums,
  selectCustomerAllowSmartico,
  selectCustomerBalanceInfo,
  selectCustomerBasicModalStatus,
  selectCustomerBetHistoryPlayers,
  selectCustomerBindingContracts,
  selectCustomerBroadcastAllowed,
  selectCustomerCasinoCompPointResetAt,
  selectCustomerCompPointsSport,
  selectCustomerCountryCode,
  selectCustomerCurrencies,
  selectCustomerEntities,
  selectCustomerFastAmounts,
  selectCustomerId,
  selectCustomerLastBetSaving,
  selectCustomerLastBetSum,
  selectCustomerLimitById,
  selectCustomerLimits,
  selectCustomerLimitsCreated,
  selectCustomerLimitsLoaded,
  selectCustomerLimitsTokenSuccess,
  selectCustomerMainCurrencies,
  selectCustomerMainCurrency,
  selectCustomerMigrationActiveStep,
  selectCustomerNotificationCenterPreferences,
  selectCustomerPreferencesBalanceVisible,
  selectCustomerPreferencesBettingOptions,
  selectCustomerPreferencesEntities,
  selectCustomerPreferencesOddsFormat,
  selectCustomerPreferencesOddsIndication,
  selectCustomerPreferencesTimeFormat,
  selectCustomerPreferencesTimeZone,
  selectCustomerPreferencesUpdated,
  selectCustomerPreferencesUpdating,
  selectCustomerSportCompPointResetAt,
  selectCustomerTokenEntities,
  selectCustomerUpdateState,
  selectCustomerWidgetMaximized,
  selectDirectAlternativeCurrencyRate,
  selectFastAmounts,
  selectFastAmountsWithCurrency,
  selectIsBetBuilderEnabled,
  selectIsCustomerWatchersChatEnabled,
  selectIsGBGStatusLoaded,
  selectIsGBGStatusNotSet,
  selectIsGBGVerificationNeeded,
  selectIsGviveMigrationInProgress,
  selectIsOnDatoMigrationInProgress,
  selectLimitPeriods,
  selectNeededConfirmation,
  selectPolicyVersion,
  selectQuickBetEnabled,
  selectQuickBetSnackBar,
  selectQuickBetSum,
  selectQuickBetTimer,
  selectReverseAlternativeCurrencyRate,
  selectShowCryptoCurrencies,
  selectSideAlternativeCurrencyRate,
  selectSmarticoHashLoading,
  selectSmarticoUserAccountId,
  selectSnackbarNotificationStorageState,
  selectTermsAndConditionsVersion,
  selectTermsAndPolicyPopupStatus,
  selectTimeFormat,
  selectTimeZone,
  selectTimeZones,
  selectTimeoutLimitActive,
  selectTimeoutLimitModalStatus,
  selectWebsocketHandledNotifications,
  showCustomerNotificationCenter
};
